import { useRole } from '../api/role.ts';
import {
  BatteryMedium,
  BarChart3,
  Flame,
  LayoutDashboard,
  LogOut,
  Moon,
  PlugZap,
  Sun,
  UtilityPole,
  Wrench,
  Building2,
  Bell,
  FileText,
  Users,
  Settings,
} from 'lucide-react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { hasPermission } from '@ste/shared';
import { useAuth } from '../api/auth.ts';
import { useLiveConnection, useLiveStore } from '../api/live.ts';
import { useProtocol, useSite } from '../api/queries.ts';
import { useConnection, useCurrentSite } from '../api/connection.ts';
import { Banner } from '../components/ui.tsx';
import { useSites } from '../api/sites.ts';
import { useIsTeamAdmin } from '../api/team.ts';
import { cx } from '../components/ui.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import type { CustomerPage } from '@ste/shared';
import logo from '../theme/assets/logo.svg?raw';
import { useTheme } from '../theme/theme.ts';
import { useCustomerView, useLocks } from '../api/customerView.ts';

interface NavItem {
  to: string;
  label: MessageKey;
  /** shorter label for the bottom navigation on phones */
  short?: MessageKey;
  icon: typeof LayoutDashboard;
  show?: boolean;
  /** page the installer may hide for customers (ADR-0020) */
  page?: CustomerPage;
  /** false: not in the bottom navigation on phones (reachable via the header) */
  bottom?: boolean;
}

export function Layout() {
  useLiveConnection();
  useSites();
  const mode = useConnection((s) => s.mode);
  const sites = useConnection((s) => s.sites);
  const siteId = useConnection((s) => s.siteId);
  const noSite = mode === 'cloud' && !siteId;
  const cloudSite = useCurrentSite();
  const location = useLocation();
  // account-level pages (STE Connect) work without a selected or reachable site
  const accountPage = ['/anlagen', '/team', '/einstellungen'].some((path) =>
    location.pathname.startsWith(path),
  );
  const site = useSite(!noSite).data;
  const protocolAvailable = useProtocol(!noSite).isSuccess;
  const status = useLiveStore((s) => s.status);
  const online = useLiveStore((s) => s.live?.online);
  const { resolved: theme, setTheme } = useTheme();
  const logout = useAuth((s) => s.logout);
  const role = useRole();
  const teamAdmin = useIsTeamAdmin();
  useCustomerView(!noSite);
  const locks = useLocks();

  const items: NavItem[] = [
    { to: '/', label: 'nav.dashboard', icon: LayoutDashboard },
    {
      to: '/laden',
      label: 'nav.charging',
      icon: PlugZap,
      page: 'charging',
      show: site?.loadpoints.some((lp) => lp.kind === 'wallbox') ?? true,
    },
    {
      to: '/waermepumpe',
      label: 'nav.heatpump',
      short: 'nav.heatpump.short',
      icon: Flame,
      page: 'heatpump',
      show: site?.loadpoints.some((lp) => lp.kind === 'heatpump') ?? false,
    },
    {
      to: '/speicher',
      label: 'nav.battery',
      icon: BatteryMedium,
      page: 'battery',
      show: site?.hasBattery ?? false,
    },
    { to: '/netz', label: 'nav.grid', icon: UtilityPole, page: 'grid' },
    { to: '/historie', label: 'nav.history', icon: BarChart3, page: 'history' },
    {
      to: '/installateur',
      label: 'nav.installer',
      short: 'nav.installer.short',
      icon: Wrench,
      show: role !== undefined && hasPermission(role, 'site:configure'),
    },
    { to: '/meldungen', label: 'nav.notifications', icon: Bell, bottom: false },
    { to: '/protokoll', label: 'nav.protocol', icon: FileText, show: protocolAvailable, bottom: false },
    { to: '/anlagen', label: 'nav.sites', icon: Building2, show: mode === 'cloud', bottom: false },
    { to: '/team', label: 'nav.team', icon: Users, show: teamAdmin, bottom: false },
    { to: '/einstellungen', label: 'nav.settings', icon: Settings, bottom: false },
  ];
  const visible = items.filter(
    (item) => item.show !== false && !(item.page && !noSite && locks.hidden(item.page)),
  );
  const connected = status === 'open' && online !== false;

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[16rem_1fr] print:block">
      {/* sidebar on large screens */}
      <aside className="hidden px-4 py-6 lg:flex lg:flex-col print:hidden">
        <div
          className="mb-8 px-3 text-text"
          dangerouslySetInnerHTML={{ __html: logo }}
          style={{ width: 150 }}
        />
        <nav className="flex flex-col gap-1" aria-label="Hauptnavigation">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cx(
                  'flex min-h-11 items-center gap-3 rounded-2xl px-3 font-medium transition',
                  isActive
                    ? 'bg-surface-2 text-accent-text shadow-inset'
                    : 'text-muted hover:bg-surface hover:text-text',
                )
              }
            >
              <item.icon size={20} aria-hidden />
              {t(item.label)}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-h-screen flex-col">
        <header className="sticky top-0 z-10 bg-bg/85 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3 backdrop-blur sm:px-6 print:hidden">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div
                className="text-text lg:hidden"
                dangerouslySetInnerHTML={{ __html: logo }}
                style={{ width: 104 }}
              />
              <div className="hidden min-w-0 sm:block">
                <SiteSwitcher title={site?.title} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <NavLink
                to="/meldungen"
                aria-label={t('nav.notifications')}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-surface text-muted shadow-raised-sm hover:text-text lg:hidden"
              >
                <Bell size={20} />
              </NavLink>
              {mode === 'cloud' && (
                <NavLink
                  to="/anlagen"
                  aria-label={t('nav.sites')}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-surface text-muted shadow-raised-sm hover:text-text lg:hidden"
                >
                  <Building2 size={20} />
                </NavLink>
              )}
              <span
                className="mx-1 flex items-center gap-2 text-sm text-muted"
                title={connected ? 'Live' : t('common.offline')}
                aria-live="polite"
              >
                <span className={cx('h-2.5 w-2.5 rounded-full', connected ? 'bg-accent' : 'bg-warm')} />
                <span className="hidden sm:inline">{connected ? 'Live' : t('common.reconnecting')}</span>
              </span>
              <button
                type="button"
                aria-label={`${t('common.theme')}: ${theme === 'dark' ? t('common.themeLight') : t('common.themeDark')}`}
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="hidden h-11 w-11 items-center justify-center rounded-full bg-surface text-muted shadow-raised-sm hover:text-text sm:inline-flex"
              >
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              {/* phones: settings (with the theme) instead of the theme toggle */}
              <NavLink
                to="/einstellungen"
                aria-label={t('nav.settings')}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-surface text-muted shadow-raised-sm hover:text-text sm:hidden"
              >
                <Settings size={20} />
              </NavLink>
              <button
                type="button"
                aria-label={t('auth.logout')}
                onClick={() => void logout()}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-surface text-muted shadow-raised-sm hover:text-text"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
          {/* phones: the site is always visible, also with only one site */}
          {(mode === 'cloud' ? sites.length > 0 : site?.title) && (
            <div className="mt-2 sm:hidden">
              <SiteSwitcher title={site?.title} wide />
            </div>
          )}
        </header>

        <main className="mx-auto w-full max-w-6xl flex-1 px-4 pt-4 pb-36 sm:px-6 lg:pb-10">
          {noSite && !accountPage ? (
            <div className="space-y-4">
              <p className="text-muted">{t('sites.noSite')}</p>
              <NavLink className="text-accent-text underline" to="/anlagen">
                {t('nav.sites')}
              </NavLink>
            </div>
          ) : (
            <>
              {mode === 'cloud' && cloudSite && !cloudSite.online && !accountPage && (
                <div className="mb-4">
                  <Banner
                    tone="warm"
                    title={cloudSite.deviceId ? t('sites.siteOffline') : t('sites.notPaired')}
                  >
                    <NavLink className="text-accent-text underline" to="/anlagen">
                      {t('nav.sites')}
                    </NavLink>
                  </Banner>
                </div>
              )}
              <Outlet />
            </>
          )}
        </main>

        {/* bottom navigation on phones and tablets */}
        <nav
          aria-label="Hauptnavigation"
          className="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-10 flex gap-1 rounded-[1.75rem] bg-surface p-1.5 shadow-raised lg:hidden print:hidden"
        >
          {visible
            .filter((item) => item.bottom !== false)
            .map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  cx(
                    'flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-[1.25rem] text-[10.5px] font-semibold tracking-tight transition',
                    isActive ? 'bg-surface-2 text-accent-text shadow-inset' : 'text-muted',
                  )
                }
              >
                <item.icon size={22} aria-hidden />
                <span className="max-w-full truncate">{t(item.short ?? item.label)}</span>
              </NavLink>
            ))}
        </nav>
      </div>
    </div>
  );
}

/** current site: a selector with several sites (STE Connect), otherwise its name */
function SiteSwitcher({ title, wide = false }: { title: string | undefined; wide?: boolean }) {
  const mode = useConnection((s) => s.mode);
  const sites = useConnection((s) => s.sites);
  const siteId = useConnection((s) => s.siteId);
  const selectSite = useConnection((s) => s.selectSite);
  const current = useCurrentSite();

  if (mode === 'cloud' && sites.length > 1) {
    return (
      <select
        aria-label={t('sites.switch')}
        className={cx(
          'min-h-11 truncate rounded-lg border border-border bg-surface-2 px-2 text-text',
          wide ? 'w-full' : 'max-w-56',
        )}
        value={siteId}
        onChange={(e) => selectSite(e.target.value)}
      >
        {sites.map((s) => (
          <option key={s.id} value={s.id}>
            {s.title}
            {s.online ? '' : ` (${t('sites.offline')})`}
          </option>
        ))}
      </select>
    );
  }
  if (mode === 'cloud') {
    // one site: its name links to the site management
    return (
      <NavLink
        to="/anlagen"
        aria-label={`${t('nav.sites')}: ${current?.title ?? ''}`}
        className="flex min-h-11 min-w-0 items-center gap-2 text-text"
      >
        <Building2 size={18} className="shrink-0 text-muted" aria-hidden />
        <span className="truncate font-medium">{current?.title ?? title}</span>
      </NavLink>
    );
  }
  return title ? (
    <span className={cx('block truncate', wide ? 'font-medium text-text' : 'text-muted lg:text-text')}>
      {title}
    </span>
  ) : null;
}
